import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    TextField,
    CircularProgress,
    FormControlLabel,
    Switch,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
    FlexDiv,
    Heading3,
    TextDefault,
    TextTiny,
    ThemedButton,
} from '@awarego-ui/components';
import { useStores } from '../../hooks/use-stores';

const LLM_OPTIONS = [
    { id: 'openai', label: 'OpenAI' },
    { id: 'claude', label: 'Claude' },
];

function buildPublicUrl(myDomain, key) {
    if (!key) return '';
    const base = String(myDomain || '').replace(/\/$/, '');
    if (!base) return '';
    return `${base}/AwareExpose/${key}`;
}

function normaliseDomain(d) {
    return String(d || '')
        .trim()
        .toLowerCase()
        .replace(/^@/, '');
}

function ChainEditor({ label, value, onChange }) {
    const handleAdd = (id) => {
        onChange([...value, id]);
    };
    const handleRemove = (index) => {
        const next = value.slice();
        next.splice(index, 1);
        onChange(next);
    };
    const handleMove = (index, delta) => {
        const next = value.slice();
        const newIndex = index + delta;
        if (newIndex < 0 || newIndex >= next.length) return;
        const [item] = next.splice(index, 1);
        next.splice(newIndex, 0, item);
        onChange(next);
    };
    return (
        <div style={{ marginBottom: 16 }}>
            <Heading3 style={{ marginBottom: 4 }}>{label}</Heading3>
            <TextTiny block style={{ marginBottom: 8 }}>
                Providers are called in order. The first call enables web search
                for the exposure scan; subsequent calls enrich the previous JSON.
            </TextTiny>
            {value.length === 0 && (
                <TextTiny block style={{ marginBottom: 8 }}>
                    No providers selected. The default chain will be used.
                </TextTiny>
            )}
            {value.map((id, idx) => {
                const opt = LLM_OPTIONS.find((o) => o.id === id) || {
                    id,
                    label: id,
                };
                return (
                    <FlexDiv
                        key={`${id}-${idx}`}
                        style={{
                            alignItems: 'center',
                            gap: 4,
                            marginBottom: 4,
                        }}
                    >
                        <Chip
                            label={`${idx + 1}. ${opt.label}`}
                            color="primary"
                            variant="outlined"
                        />
                        <IconButton
                            size="small"
                            onClick={() => handleMove(idx, -1)}
                            disabled={idx === 0}
                        >
                            <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => handleMove(idx, 1)}
                            disabled={idx === value.length - 1}
                        >
                            <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => handleRemove(idx)}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </FlexDiv>
                );
            })}
            <FlexDiv style={{ alignItems: 'center', gap: 8, marginTop: 8 }}>
                <TextTiny>Add provider:</TextTiny>
                {LLM_OPTIONS.map((opt) => (
                    <ThemedButton
                        key={opt.id}
                        mode="secondary"
                        size="tiny"
                        $noTextTransform
                        text={`+ ${opt.label}`}
                        onClick={() => handleAdd(opt.id)}
                    />
                ))}
            </FlexDiv>
        </div>
    );
}

const AwareExposeSettingsModal = observer(({ open, onClose, companyId }) => {
    const { companyStore, authStore, commonStore, brandingStore } = useStores();
    const { awareExposeConfig, loadingAwareExposeConfig } = companyStore;
    const { currentUser } = authStore;
    const myDomain = brandingStore && brandingStore.myDomain;

    const [enabled, setEnabled] = useState(false);
    const [domainsDraft, setDomainsDraft] = useState([]);
    const [domainInput, setDomainInput] = useState('');
    const [chainScan, setChainScan] = useState(['openai']);
    const [chainAttacks, setChainAttacks] = useState(['openai']);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (open && companyId) {
            companyStore.loadAwareExposeConfig(companyId);
        }
    }, [open, companyId]);

    useEffect(() => {
        if (!awareExposeConfig) return;
        setEnabled(!!awareExposeConfig.enabled);
        const domains = Array.isArray(awareExposeConfig.allowedDomains)
            ? awareExposeConfig.allowedDomains
            : [];
        if (domains.length === 0 && currentUser && currentUser.email) {
            const dom = currentUser.email.split('@')[1];
            setDomainsDraft(dom ? [normaliseDomain(dom)] : []);
        } else {
            setDomainsDraft(domains);
        }
        setChainScan(
            Array.isArray(awareExposeConfig.chainScan) &&
                awareExposeConfig.chainScan.length
                ? awareExposeConfig.chainScan
                : ['openai']
        );
        setChainAttacks(
            Array.isArray(awareExposeConfig.chainAttacks) &&
                awareExposeConfig.chainAttacks.length
                ? awareExposeConfig.chainAttacks
                : ['openai']
        );
    }, [awareExposeConfig, currentUser]);

    const publicUrl = useMemo(
        () =>
            buildPublicUrl(
                myDomain,
                awareExposeConfig && awareExposeConfig.publicUrlKey
            ),
        [myDomain, awareExposeConfig]
    );

    const handleAddDomain = () => {
        const next = normaliseDomain(domainInput);
        if (!next) return;
        if (!domainsDraft.includes(next)) {
            setDomainsDraft([...domainsDraft, next]);
        }
        setDomainInput('');
    };

    const handleRemoveDomain = (d) => {
        setDomainsDraft(domainsDraft.filter((x) => x !== d));
    };

    const handleSave = async () => {
        try {
            await companyStore.saveAwareExposeSettings(companyId, {
                enabled,
                allowedDomains: domainsDraft,
                chainScan,
                chainAttacks,
            });
            commonStore.success && commonStore.success('Settings saved');
            onClose();
        } catch (e) {
            commonStore.error &&
                commonStore.error(
                    companyStore.error || 'Failed to save settings'
                );
        }
    };

    const handleCopy = async () => {
        if (!publicUrl) return;
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* ignore */
        }
    };

    const handleRotate = async () => {
        if (
            !window.confirm(
                'Rotate the public URL? The existing link will stop working immediately.'
            )
        ) {
            return;
        }
        try {
            await companyStore.rotateAwareExposeKey(companyId);
        } catch (e) {
            commonStore.error &&
                commonStore.error(
                    companyStore.error || 'Failed to rotate public URL'
                );
        }
    };

    const saving = companyStore.savingAwareExposeSettings.includes(companyId);
    const rotating = companyStore.rotatingAwareExposeKey.includes(companyId);

    return (
        <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>AwareExpose</DialogTitle>
            <DialogContent dividers>
                {loadingAwareExposeConfig && !awareExposeConfig ? (
                    <FlexDiv center style={{ padding: 32 }}>
                        <CircularProgress size={24} />
                    </FlexDiv>
                ) : (
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enabled}
                                    onChange={(e) =>
                                        setEnabled(e.target.checked)
                                    }
                                    color="primary"
                                />
                            }
                            label={
                                enabled
                                    ? 'AwareExpose is enabled'
                                    : 'AwareExpose is disabled'
                            }
                        />
                        <TextTiny block style={{ marginBottom: 16 }}>
                            When enabled, employees can use AwareExpose from the
                            MY portal and via the public link below.
                        </TextTiny>

                        <Heading3 style={{ marginTop: 16, marginBottom: 8 }}>
                            Whitelist email domains
                        </Heading3>
                        <TextTiny block style={{ marginBottom: 8 }}>
                            Only emails matching these domains can submit the
                            AwareExpose form. Prefilled with the current admin
                            domain.
                        </TextTiny>
                        <FlexDiv
                            style={{
                                flexWrap: 'wrap',
                                gap: 8,
                                marginBottom: 8,
                            }}
                        >
                            {domainsDraft.map((d) => (
                                <Chip
                                    key={d}
                                    label={d}
                                    onDelete={() => handleRemoveDomain(d)}
                                />
                            ))}
                        </FlexDiv>
                        <FlexDiv style={{ alignItems: 'center', gap: 8 }}>
                            <TextField
                                size="small"
                                placeholder="e.g. awarego.com"
                                value={domainInput}
                                onChange={(e) =>
                                    setDomainInput(e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddDomain();
                                    }
                                }}
                                fullWidth
                            />
                            <IconButton onClick={handleAddDomain}>
                                <AddIcon />
                            </IconButton>
                        </FlexDiv>

                        <Heading3 style={{ marginTop: 24, marginBottom: 8 }}>
                            Public URL
                        </Heading3>
                        <TextTiny block style={{ marginBottom: 8 }}>
                            Share this link with employees. Anonymous use does
                            not store any results.
                        </TextTiny>
                        <FlexDiv style={{ alignItems: 'center', gap: 8 }}>
                            <TextField
                                size="small"
                                value={publicUrl || ''}
                                placeholder="Save once with AwareExpose enabled to generate"
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                            <Tooltip title={copied ? 'Copied' : 'Copy link'}>
                                <span>
                                    <IconButton
                                        disabled={!publicUrl}
                                        onClick={handleCopy}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Rotate public URL (invalidates old link)">
                                <span>
                                    <IconButton
                                        disabled={!publicUrl || rotating}
                                        onClick={handleRotate}
                                    >
                                        {rotating ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <RefreshIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </FlexDiv>

                        <Heading3 style={{ marginTop: 24, marginBottom: 8 }}>
                            LLM chain — Exposure scan
                        </Heading3>
                        <ChainEditor
                            label="Providers used to discover public signals (web search on first call)"
                            value={chainScan}
                            onChange={setChainScan}
                        />

                        <Heading3 style={{ marginTop: 24, marginBottom: 8 }}>
                            LLM chain — Attack emails
                        </Heading3>
                        <ChainEditor
                            label="Providers used to generate the three educational phishing emails"
                            value={chainAttacks}
                            onChange={setChainAttacks}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <ThemedButton
                    mode="secondary"
                    text="Cancel"
                    onClick={onClose}
                    disabled={saving}
                />
                <ThemedButton
                    text={saving ? 'Saving…' : 'Save'}
                    onClick={handleSave}
                    disabled={saving}
                />
            </DialogActions>
        </Dialog>
    );
});

export default AwareExposeSettingsModal;
